import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Fish } from 'lucide-react';
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip as RTooltip,
  ResponsiveContainer, CartesianGrid, ReferenceLine,
} from 'recharts';

const STATUS_COLORS = {
  breached: '#ef4444',
  warning: '#f59e0b',
  ok: '#10b981',
  none: '#60a5fa',
};

const Y_AXIS_WIDTH = 118;

// Deterministic jitter from the deal id so dots don't stack on the stage lane.
const jitter = (id) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return ((Math.abs(h) % 100) / 100 - 0.5) * 0.5;
};

// Fish faces left — swimming toward the boat's hook at the top left.
// Swims in from the plot's left edge on load, staggered per fish.
function FishShape({ cx, cy, index, payload, swim, onOpen }) {
  if (cx == null || cy == null) return null;
  const color = STATUS_COLORS[payload.status] || STATUS_COLORS.none;
  if (payload.caught) {
    // Caught at Closing — the fish hangs off the boat's hook, wriggling.
    const hx = Y_AXIS_WIDTH + 38 + payload.caught_index * 17;
    const hy = 62 + payload.caught_index * 5;
    return (
      <g transform={`translate(${hx - 3},${hy - 7})`} style={{ cursor: 'pointer' }}
        onClick={() => onOpen(payload.project_id)} data-testid={`timing-fish-${payload.project_id}`}>
        <g className="fish-caught">
          <path d="M1.5 7 C4 3 9 2.5 12 7 C9 11.5 4 11 1.5 7 Z" fill={color} opacity="0.9" />
          <path d="M12 7 L16.5 3.5 L15.2 7 L16.5 10.5 Z" fill={color} opacity="0.75" />
          <circle cx="4" cy="6" r="0.9" fill="#0b1220" />
        </g>
      </g>
    );
  }
  const swimFrom = -(Math.max(cx - Y_AXIS_WIDTH, 40));
  return (
    <g transform={`translate(${cx - 8},${cy - 7})`} style={{ cursor: 'pointer' }}
      onClick={() => onOpen(payload.project_id)} data-testid={`timing-fish-${payload.project_id}`}>
      <g className={swim ? 'fish-swim-in' : ''}
        style={swim
          ? { '--swim-from': `${swimFrom}px`, animationDelay: `${(index || 0) * 0.12}s` }
          : { opacity: 0 }}>
        {/* body */}
        <path d="M1.5 7 C4 3 9 2.5 12 7 C9 11.5 4 11 1.5 7 Z" fill={color} opacity="0.9" />
        {/* tail */}
        <path d="M12 7 L16.5 3.5 L15.2 7 L16.5 10.5 Z" fill={color} opacity="0.75" />
        {/* eye */}
        <circle cx="4" cy="6" r="0.9" fill="#0b1220" />
      </g>
    </g>
  );
}

// Boat with fishing rod at the top left — the hook waits where deals get "caught" (Closing).
function BoatOverlay() {
  return (
    <svg width="160" height="110" viewBox="0 0 160 110" data-testid="timing-boat"
      className="absolute pointer-events-none" style={{ left: Y_AXIS_WIDTH - 66, top: 0 }}>
      {/* waves */}
      <path d="M2 33 Q 10 29 18 33 T 34 33 T 50 33 T 66 33 T 82 33 T 98 33"
        stroke="#38bdf8" strokeOpacity="0.3" fill="none" strokeWidth="1.5" />
      {/* hull */}
      <path d="M10 25 L17 33 L62 33 L72 25 Z" fill="#64748b" />
      {/* cabin */}
      <rect x="28" y="14" width="19" height="11" rx="1" fill="#475569" />
      <rect x="32" y="17" width="5" height="4" rx="0.5" fill="#93c5fd" opacity="0.8" />
      {/* rod */}
      <path d="M45 14 L104 3" stroke="#a8a29e" strokeWidth="1.6" fill="none" strokeLinecap="round" />
      {/* fishing line */}
      <path d="M104 3 L104 52" stroke="#cbd5e1" strokeOpacity="0.5" strokeWidth="1" />
      {/* hook */}
      <path d="M104 52 q0 7 -6 7 q-5 0 -5 -5" stroke="#cbd5e1" strokeOpacity="0.8" strokeWidth="1.4"
        fill="none" strokeLinecap="round" />
    </svg>
  );
}

function TimingTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-card border border-border rounded-sm p-3 text-xs shadow-lg" style={{ maxWidth: 240 }}>
      <p className="font-medium text-foreground">{d.deal_name || d.deal_label}</p>
      {d.deal_name && d.deal_label !== d.deal_name && (
        <p className="text-muted-foreground font-mono text-[10px]">{d.deal_label}</p>
      )}
      <p className="text-muted-foreground mt-1">
        {d.stage_label} · <span className="text-foreground font-mono">{d.days_in_stage}d</span> in stage
        {d.sla_days != null && <span> (SLA {d.sla_days} bd)</span>}
      </p>
      {d.avg_days != null && (
        <p className={d.days_in_stage > d.avg_days ? 'text-amber-400' : 'text-sky-400'}>
          Team average here: {d.avg_days}d
          {d.days_in_stage > d.avg_days
            ? ` — ${Math.round((d.days_in_stage - d.avg_days) * 10) / 10}d slower than usual`
            : ' — on pace'}
        </p>
      )}
      <p className="text-muted-foreground">{d.owner_name || 'Unassigned'}</p>
      <p className="mt-0.5 font-medium" style={{ color: STATUS_COLORS[d.status] }}>
        {d.status === 'breached' ? 'SLA breached' : d.status === 'warning' ? 'SLA expiring soon' : d.status === 'ok' ? 'On track' : 'No SLA'}
      </p>
      <p className="text-[10px] text-muted-foreground/60 mt-1">Click the fish to open the deal</p>
    </div>
  );
}

export function PipelineTimingChart() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [swim, setSwim] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    api.getPipelineTiming().then((res) => setData(res.data)).catch(() => {});
  }, []);

  // Fish swim in only once the chart scrolls into view.
  useEffect(() => {
    const el = containerRef.current;
    if (!data || !el || swim) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setSwim(true); obs.disconnect(); }
    }, { threshold: 0.25 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [data, swim]);

  if (!data || !data.deals?.length) return null;
  const { stages, deals } = data;
  let caughtCount = 0;
  const points = deals.map((d) => {
    const caught = d.stage_index === stages.length - 1;
    return {
      ...d,
      caught,
      caught_index: caught ? caughtCount++ : 0,
      x: d.days_in_stage,
      y: d.stage_index + jitter(d.project_id),
      stage_label: stages[d.stage_index]?.label || d.stage_slug,
      avg_days: stages[d.stage_index]?.avg_days ?? null,
    };
  });
  const maxDays = Math.max(...points.map((p) => p.x), ...stages.map((s) => s.sla_days || 0),
    ...stages.map((s) => s.avg_days || 0));
  const hasAvg = stages.some((s) => s.avg_days != null);
  const chartHeight = Math.max(300, stages.length * 32 + 80);

  return (
    <div className="bg-card border border-border rounded-sm p-4 sm:p-5" data-testid="pipeline-timing-chart">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-1">
        <h3 className="font-heading text-lg text-foreground flex items-center gap-2">
          <Fish className="w-4 h-4 text-blue-400" /> Pipeline Timing
        </h3>
        <span className="text-xs text-muted-foreground">
          Every fish is a deal — each day in a stage it drifts further right; advancing pulls it back left, one row closer to the boat
        </span>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-1 text-[10px] text-muted-foreground">
        <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: STATUS_COLORS.ok }} />On track</span>
        <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: STATUS_COLORS.warning }} />SLA expiring</span>
        <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: STATUS_COLORS.breached }} />SLA breached</span>
        <span><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: STATUS_COLORS.none }} />No SLA</span>
        <span className="text-muted-foreground/60">— dashed red = SLA limit per stage</span>
        {hasAvg && <span className="text-sky-400/80">— dashed blue = team average per stage</span>}
      </div>
      {hasAvg && (
        <p className="text-[10px] text-muted-foreground/60 mb-1" data-testid="timing-avg-explainer">
          The blue marker shows how long deals have historically taken in that stage (average of all completed
          runs). A fish drifting well past the blue marker is stuck longer than usual — worth a nudge.
        </p>
      )}
      <div className="relative" ref={containerRef}>
        <BoatOverlay />
        <ResponsiveContainer width="100%" height={chartHeight}>
          <ScatterChart margin={{ top: 42, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.08)" horizontal vertical={false} />
            <XAxis type="number" dataKey="x" domain={[0, Math.ceil(maxDays * 1.15) || 5]}
              tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false}
              axisLine={{ stroke: 'rgba(148,163,184,0.2)' }}
              label={{ value: 'Days in current stage', position: 'insideBottomRight', offset: -3, style: { fontSize: 10, fill: '#94a3b8' } }} />
            <YAxis type="number" dataKey="y" domain={[-0.6, stages.length - 0.4]} width={Y_AXIS_WIDTH}
              ticks={stages.map((_, i) => i)}
              tickFormatter={(i) => stages[i]?.label || ''}
              tick={{ fontSize: 9, fill: '#94a3b8' }} tickLine={false} axisLine={false} interval={0} />
            <ZAxis range={[80, 80]} />
            {stages.map((s, i) => s.sla_days ? (
              <ReferenceLine key={s.slug} stroke="#ef4444" strokeDasharray="4 3" strokeOpacity={0.5}
                segment={[{ x: s.sla_days, y: i - 0.38 }, { x: s.sla_days, y: i + 0.38 }]} />
            ) : null)}
            {stages.map((s, i) => s.avg_days != null ? (
              <ReferenceLine key={`avg-${s.slug}`} stroke="#38bdf8" strokeDasharray="2 4" strokeOpacity={0.7}
                segment={[{ x: s.avg_days, y: i - 0.38 }, { x: s.avg_days, y: i + 0.38 }]} />
            ) : null)}
            <RTooltip content={<TimingTooltip />} cursor={{ strokeDasharray: '3 3', stroke: 'rgba(148,163,184,0.3)' }} />
            <Scatter data={points} shape={(props) => (
              <FishShape {...props} swim={swim} onOpen={(id) => navigate(`/projects/${id}`)} />
            )} isAnimationActive={false} />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
